import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  LuChevronLeft,
  LuChevronRight,
  LuCompass,
  LuMouse,
  LuUsers,
} from 'react-icons/lu';
import { useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/Init';
import AppLayout from '../components/oracle/AppLayout';
import LogoSpinner from '../components/oracle/LogoSpinner';
import EmptyState from '../components/oracle/EmptyState';
import PageHeader from '../components/oracle/PageHeader';
import SegmentedSwitch from '../components/oracle/SegmentedSwitch';
import ExploreComparisonCard from '../components/glowup/ExploreComparisonCard';
import ExploreTeaserCard from '../components/glowup/ExploreTeaserCard';
import ExploreEncouragementRail from '../components/glowup/ExploreEncouragementRail';
import DeckTransition from '../components/oracle/DeckTransition';
import { COPY } from '../contracts/copyMap';
import { PEER_SCORE_VOTE_QUOTA } from '../contracts/glowupApi';
import { isPaywalled } from '../contracts/billingApi';
import MembershipCTA from '../components/billing/MembershipCTA';

// EXPLORE — the judge feed (route /judge; the sidebar labels it EXPLORE).
// Rebuilt to the design-recreation mockup (desktop 689-875, mobile 1313-1445)
// with honest data only — no fabricated vote counts, scores, or regimens.
//
//   ┌──────────────────────────────────────────────┬──────────────┐
//   │ EXPLORE header        ‹ PREVIOUS │ NEXT › ───┼─ mr aligns   │
//   ├──────────────────────────────────────────────┤  with rail   │
//   │ ExploreComparisonCard (BEFORE / AFTER)       │ Encourage-   │
//   │ SCROLL FOR NEXT GLOWUP hint                  │ ment rail    │
//   │ ExploreTeaserCard (next card, dimmed)        │ (340px, lg+) │
//   └──────────────────────────────────────────────┴──────────────┘
//
// Data flow (unchanged backend):
//   getGlowupDeck            -> { status, deck: GlowupDeckCard[] }
//   submitGlowupFeedback     -> { status, responses_total_for_user,
//                                 reached_quota }
//
// Navigation is local to the fetched deck:
//   SEND ENCOURAGEMENT  submits { glowup_id, selected_vectors,
//                       direction:'right' }, shows ENCOURAGEMENT SENT ~1.2s,
//                       then advances + resets hearts.
//   SKIP / NEXT pill    advance the cursor without submitting.
//   PREVIOUS pill       steps back (disabled at index 0).

const GlowupJudge = () => {
  const [state, setState] = useState({ phase: 'loading' });
  const [cursor, setCursor] = useState(0);
  // 1 = advancing through the deck, -1 = stepping back with the ‹ pill.
  const [direction, setDirection] = useState(1);
  // goTo compares against the live cursor without taking it as a dependency —
  // the callback is passed to memoised children and must stay stable.
  const cursorRef = useRef(0);
  useEffect(() => { cursorRef.current = cursor; }, [cursor]);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [judgmentsTotal, setJudgmentsTotal] = useState(0);
  const [reachedQuota, setReachedQuota] = useState(false);
  const confirmTimer = useRef(null);
  // Guards against overlapping append fetches while the judge advances quickly.
  const appendingRef = useRef(false);

  useEffect(() => () => clearTimeout(confirmTimer.current), []);

  const loadDeck = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const getDeck = httpsCallable(functions, 'getGlowupDeck');
      const result = await getDeck({});
      const data = result && result.data;
      if (data && data.status === 'success') {
        setState({
          phase: 'ready',
          deck: data.deck || [],
          nextCursor: data.next_cursor || null,
        });
        setCursor(0);
        setSelected([]);
        return;
      }
      if (isPaywalled(data)) {
        // Not an error phase: retrying a paywall fails identically forever.
        setState({ phase: 'paywalled' });
      } else if (data && data.error_code === 'DECK_EMPTY') {
        setState({ phase: 'empty' });
        return;
      }
      setState({
        phase: 'error',
        message: (data && data.error_message) || COPY.REJECT_NETWORK_SUBTITLE,
      });
    } catch (err) {
      setState({
        phase: 'error',
        message: (err && err.message) || COPY.REJECT_NETWORK_SUBTITLE,
      });
    }
  }, []);

  useEffect(() => {
    loadDeck();
  }, [loadDeck]);

  // Local deck navigation — no submission. Used by SKIP and the ‹ › pills.
  //
  // Records which way the deck is travelling so DeckTransition can send the
  // outgoing card off the correct edge. Derived from the target index rather
  // than tracked separately, so the ‹ pill genuinely reverses the motion
  // instead of every move looking like a forward one.
  const goTo = useCallback((index) => {
    clearTimeout(confirmTimer.current);
    setConfirmation(false);
    setSelected([]);
    setDirection((prev) => {
      const next = index > cursorRef.current ? 1 : index < cursorRef.current ? -1 : prev;
      return next;
    });
    setCursor(index);
  }, []);

  const send = useCallback(async () => {
    if (submitting || confirmation || state.phase !== 'ready') return;
    const card = state.deck[cursor];
    if (!card || selected.length === 0) return;
    setSubmitting(true);
    try {
      const fn = httpsCallable(functions, 'submitGlowupFeedback');
      const result = await fn({
        glowup_id: card.glowup_id,
        selected_vectors: selected,
        direction: 'right',
      });
      const data = result && result.data;
      if (data && data.status === 'success') {
        setJudgmentsTotal(data.responses_total_for_user);
        if (data.reached_quota) setReachedQuota(true);
      }
      setConfirmation(true);
      confirmTimer.current = setTimeout(() => {
        setConfirmation(false);
        setSelected([]);
        setDirection(1); // a sent judgment always advances
        setCursor((c) => c + 1);
      }, 1200);
    } catch (err) {
      // Keep the card in place so the judge can retry.
    } finally {
      setSubmitting(false);
    }
  }, [submitting, confirmation, state, cursor, selected]);

  const handleToggle = useCallback((target) => {
    setSelected((prev) =>
      prev.includes(target) ? prev.filter((t) => t !== target) : [...prev, target],
    );
  }, []);

  // Seamless pagination: fetch and append the next page while the judge is
  // still a few cards from the end, so EXPLORE never dead-ends mid-session.
  const appendNextPage = useCallback(async () => {
    if (state.phase !== 'ready' || !state.nextCursor || appendingRef.current) return;
    appendingRef.current = true;
    try {
      const getDeck = httpsCallable(functions, 'getGlowupDeck');
      const result = await getDeck({ cursor: state.nextCursor });
      const data = result && result.data;
      if (data && data.status === 'success') {
        setState((prev) => {
          if (prev.phase !== 'ready') return prev;
          return {
            phase: 'ready',
            deck: [...prev.deck, ...(data.deck || [])],
            nextCursor: data.next_cursor || null,
          };
        });
      } else {
        // DECK_EMPTY or a soft error on a continuation → stop paginating.
        setState((prev) =>
          prev.phase === 'ready' ? { ...prev, nextCursor: null } : prev,
        );
      }
    } catch (err) {
      // Transient — leave nextCursor so a later advance retries.
    } finally {
      appendingRef.current = false;
    }
  }, [state]);

  const PREFETCH_THRESHOLD = 3;
  useEffect(() => {
    if (state.phase !== 'ready' || !state.nextCursor) return;
    if (cursor >= state.deck.length - PREFETCH_THRESHOLD) {
      appendNextPage();
    }
  }, [cursor, state, appendNextPage]);

  if (state.phase === 'loading') {
    return (
      <ExploreFrame>
        <div className="min-h-[50vh] flex items-center justify-center">
          <LogoSpinner />
        </div>
      </ExploreFrame>
    );
  }
  if (state.phase === 'error') {
    return (
      <ExploreFrame>
        <div className="min-h-[45vh] flex items-center justify-center">
          <EmptyState
            headline="UNABLE TO LOAD"
            subtitle={state.message}
            ctaLabel={COPY.RETRY_CTA}
            onCta={loadDeck}
            icon={<LuCompass size={22} />}
          />
        </div>
      </ExploreFrame>
    );
  }
  // Ran past the loaded deck but another page is inbound — hold on a spinner
  // instead of flashing the empty state.
  if (state.phase === 'ready' && cursor >= state.deck.length && state.nextCursor) {
    return (
      <ExploreFrame>
        <div className="min-h-[50vh] flex items-center justify-center">
          <LogoSpinner />
        </div>
      </ExploreFrame>
    );
  }
  if (state.phase === 'paywalled') {
    return (
      <ExploreFrame>
        <MembershipCTA reason="glowup" />
      </ExploreFrame>
    );
  }
  if (state.phase === 'empty' || cursor >= state.deck.length) {
    return (
      <ExploreFrame>
        <div className="min-h-[45vh] flex items-center justify-center">
          <EmptyState
            headline={COPY.JUDGE_DECK_EMPTY_HEADLINE || 'THE QUEUE IS QUIET'}
            subtitle={COPY.JUDGE_DECK_EMPTY_SUBTITLE || 'New glowups arrive throughout the day.'}
            ctaLabel={COPY.RETRY_CTA}
            onCta={loadDeck}
            icon={<LuCompass size={22} />}
          />
        </div>
      </ExploreFrame>
    );
  }

  const card = state.deck[cursor];
  const nextCard = state.deck[cursor + 1];

  const footnote = (judgmentsTotal > 0 || reachedQuota) ? (
    <p
      className="font-display uppercase tracking-[0.18em] text-bone-dim text-center mt-4 mb-0"
      style={{ fontSize: 10 }}
    >
      {reachedQuota
        ? 'PEER SCORE UNLOCKED'
        : `${COPY.JUDGE_QUOTA_LABEL} · ${String(judgmentsTotal).padStart(2, '0')} / ${String(PEER_SCORE_VOTE_QUOTA).padStart(2, '0')}`}
    </p>
  ) : null;

  return (
    <ExploreFrame
      headerRight={(
        /* prev / next pills — mr aligns with the comparison card edge
           (rail 340px + 28px gap) */
        <div className="hidden lg:flex items-center gap-2.5 mt-[18px] lg:mr-[368px] shrink-0">
          <NavPill
            onClick={cursor > 0 ? () => goTo(cursor - 1) : undefined}
            disabled={cursor === 0}
            ariaLabel="Previous glowup"
          >
            <LuChevronLeft size={16} aria-hidden="true" />
            <span>{COPY.JUDGE_PREV_LABEL}</span>
          </NavPill>
          <NavPill onClick={() => goTo(cursor + 1)} ariaLabel="Next glowup">
            <span>{COPY.JUDGE_NEXT_LABEL}</span>
            <LuChevronRight size={16} aria-hidden="true" />
          </NavPill>
        </div>
      )}
    >

        {/* comparison column + encouragement rail */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-7 items-start mt-7 lg:mt-[34px]">
          <div className="flex-1 min-w-0 w-full">
            <DeckTransition cardKey={card.glowup_id} direction={direction}>
              <ExploreComparisonCard card={card} />
            </DeckTransition>

            {nextCard && (
              <div className="hidden lg:block">
                <div className="flex items-center justify-center gap-[11px] mt-[22px] text-bone-dim">
                  <LuMouse size={15} aria-hidden="true" />
                  <span
                    className="font-display uppercase tracking-[0.24em]"
                    style={{ fontSize: 11 }}
                  >
                    {COPY.JUDGE_SCROLL_HINT}
                  </span>
                </div>
                <ExploreTeaserCard card={nextCard} variant="desktop" className="mt-[26px]" />
              </div>
            )}
          </div>

          <ExploreEncouragementRail
            selected={selected}
            onToggle={handleToggle}
            onSubmit={send}
            onSkip={() => goTo(cursor + 1)}
            submitting={submitting}
            confirmation={confirmation}
            footnote={footnote}
          />
        </div>

      {/* mobile next-glowup teaser */}
      {nextCard && (
        <ExploreTeaserCard card={nextCard} variant="mobile" className="lg:hidden mt-6" />
      )}
    </ExploreFrame>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────

const NavPill = ({ children, onClick, disabled = false, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    className={[
      'flex items-center gap-2.5 bg-transparent border rounded-full px-[18px] py-3',
      'font-display uppercase tracking-[0.22em]',
      'transition-colors duration-200 ease-mogged',
      disabled
        ? 'border-white/10 text-vein-dim cursor-not-allowed'
        : 'border-white/20 text-[#D8D2C6] cursor-pointer hover:border-white/50 hover:text-bone',
    ].join(' ')}
    style={{ fontSize: 11 }}
  >
    {children}
  </button>
);

// Deck switch — the counterpart to the one on the scan deck. Uses a plain
// link-style navigate rather than sharing a component with ScanJudge, because
// the two pages own separate AppLayouts and there is nothing else to share.
const GlowupModeSwitch = () => {
  const [, setSearchParams] = useSearchParams();
  return (
    <div className="mt-4 lg:mt-0">
      <SegmentedSwitch
        options={[
          { key: 'scan', label: COPY.SCAN_JUDGE_TAB },
          { key: 'glowup', label: COPY.GLOWUP_JUDGE_TAB },
        ]}
        active="glowup"
        size="sm"
        stretch
        onChange={(key) => { if (key === 'scan') setSearchParams({}, { replace: true }); }}
      />
    </div>
  );
};

// Page frame shared by every state (ready / loading / error / empty) so the
// EXPLORE header never disappears while the deck resolves.
const ExploreFrame = ({ headerRight = null, children }) => (
  <AppLayout
    header={(
      <PageHeader
        title="EXPLORE"
        subtitle={COPY.JUDGE_FEED_SUBTITLE}
        rule={false}
        // Mirrors the switch on the scan deck so the two are mutually
        // reachable. Without it this page is a one-way door: /judge lands on
        // portraits, and ?mode=glowup would have no way back.
        mobileExtra={<GlowupModeSwitch />}
        desktopRight={headerRight}
        desktopExtra={(
          <div
            className="flex items-center gap-3 mt-4 text-[#B8B2A6] font-display"
            style={{ fontSize: 15 }}
          >
            <LuUsers size={15} className="text-bone-dim" aria-hidden="true" />
            <span>Anonymous community</span>
            <span className="text-vein-dim">·</span>
            <span>Encouragement only</span>
            <span className="text-vein-dim">·</span>
            <span>Positive votes</span>
          </div>
        )}
      />
    )}
  >
    <div className="px-5 pb-10 lg:px-12 lg:pb-14">
      {children}
    </div>
  </AppLayout>
);

export default GlowupJudge;
